import { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import { MARKETING } from '@/components/website/Marketing/marketing-tokens';
import ShowroomTile from './ShowroomTile';

export default function FeaturedShowrooms({ id, heading = 'Featured Showrooms', showrooms = [] }) {
  const [activeTileId, setActiveTileId] = useState(null);
  const sectionRef = useRef(null);

  const deactivateTiles = useCallback(() => {
    setActiveTileId(null);
  }, []);

  useEffect(() => {
    if (!activeTileId) return undefined;

    const handlePointerDown = event => {
      if (sectionRef.current?.contains(event.target)) return;
      deactivateTiles();
    };

    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [activeTileId, deactivateTiles]);

  if (!showrooms.length) return null;

  return (
    <Box
      ref={sectionRef}
      component="section"
      id={id || undefined}
      aria-labelledby="featured-showrooms-heading"
      sx={{
        width: '100%',
        maxWidth: '100%',
        py: { xs: 3, sm: 4, md: 5 },
        flex: '0 0 auto',
        bgcolor: MARKETING.canvas,
        color: MARKETING.text,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: { xs: 'flex-start', md: 'center' },
      }}
    >
      <Box
        sx={{
          textAlign: 'center',
          width: '100%',
          px: { xs: 2, sm: 3, md: 6 },
          mb: { xs: 3, md: 4 },
        }}
      >
        <Typography
          id="featured-showrooms-heading"
          component="h2"
          sx={{
            textTransform: 'uppercase',
            letterSpacing: '0.2em',
            fontWeight: 500,
            fontSize: { xs: 14, md: 15 },
          }}
        >
          {heading}
        </Typography>
        <Box
          sx={{
            width: 48,
            height: 3,
            bgcolor: MARKETING.text,
            mx: 'auto',
            mt: 1.5,
          }}
        />
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          width: '100%',
        }}
      >
        {showrooms.map(showroom => (
          <ShowroomTile
            key={showroom.id}
            showroom={showroom}
            isActive={activeTileId === showroom.id}
            onActivate={() => setActiveTileId(showroom.id)}
            onDeactivate={deactivateTiles}
          />
        ))}
      </Box>
    </Box>
  );
}

FeaturedShowrooms.propTypes = {
  id: PropTypes.string,
  heading: PropTypes.string,
  showrooms: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      city: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      region: PropTypes.string,
      mapHref: PropTypes.string,
      exteriorImage: PropTypes.string.isRequired,
      interiorSlides: PropTypes.array,
    }),
  ),
};
